import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { t } from 'services/i18n.service';
import { TAB } from 'constants/general';
import { FLAG, INVALID_FEATURE } from 'enums';
import { createHtml, increment } from 'utils/helpers';
import { decodeHtmlEntities } from 'utils/validators';
import Chip from 'components/ui/Chip';
import BaseButton from 'components/ui/BaseButton';
import { updateActiveTabIndex } from 'store/reducer/setting/slice';
import { getRoutineTabs } from 'utils/settings.util';
import {
  canNavigateToValidationError,
  getValidationErrorTabIndex,
  scrollToHabitValidationError,
  scrollToTimingValidationError
} from 'utils/validation-error-navigation.util';
import { ValidationError } from 'interfaces/setting.interface';

const ValidationErrors = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const dispatch = useAppDispatch();
  const { errors, tabs, platform, flags, isUserAdmin, customRoutine } =
    useAppSelectorShallow((state) => ({
      errors: state.setting.validationErrors,
      tabs: state.setting.tabs,
      platform: state.setting.platform,
      flags: state.setting.flags,
      isUserAdmin: state.user.isUserAdmin,
      customRoutine: state.setting.customRoutine
    }));

  const routineTabs = useMemo(
    () =>
      getRoutineTabs(
        tabs,
        platform,
        flags.includes(FLAG.GEEK_MODE),
        isUserAdmin,
        customRoutine,
        flags
      ),
    [tabs, platform, flags, isUserAdmin, customRoutine]
  );

  const getNavigationTabIndex = useCallback(
    (error: ValidationError): number => {
      if (error.feature === INVALID_FEATURE.BREAK_FREQUENCY) {
        return routineTabs.indexOf(TAB.MICRO_BREAKS);
      }

      return getValidationErrorTabIndex(error, routineTabs);
    },
    [routineTabs]
  );

  const handleErrorClick = useCallback(
    (error: ValidationError) => {
      if (!canNavigateToValidationError(error)) {
        return;
      }

      const tabIndex = getNavigationTabIndex(error);
      if (tabIndex >= 0) {
        dispatch(updateActiveTabIndex(tabIndex));
      }

      window.requestAnimationFrame(() => {
        if (error.activityId) {
          scrollToHabitValidationError(error.activityId);
          return;
        }

        scrollToTimingValidationError(error.feature);
      });
    },
    [dispatch, getNavigationTabIndex]
  );

  const getHabitLabelForError = (error: ValidationError): string =>
    error.habitName?.trim() ||
    error.tab.habit?.trim() ||
    t('validation.untitled_habit');

  const appendHabitNameToLabel = (
    label: string,
    error: ValidationError
  ): string => {
    const shouldAppend =
      Boolean(error.activityId) || error.habitPosition !== undefined;

    if (!shouldAppend) {
      return label;
    }

    return `${label} ${getHabitLabelForError(error)}`;
  };

  const getPositionLabel = (error: ValidationError): string => {
    if (
      error.habitPosition !== undefined &&
      error.customRoutinePosition !== undefined
    ) {
      return appendHabitNameToLabel(
        `${t('validation.routine_position', {
          position: increment(error.customRoutinePosition)
        })} ${t('validation.habit_position', {
          position: increment(error.habitPosition)
        })}`,
        error
      );
    }

    if (error?.position !== undefined) {
      return appendHabitNameToLabel(
        t(
          error.tab.title === TAB.CUSTOM_ROUTINES
            ? 'validation.routine_position'
            : 'validation.habit_position',
          {
            position: increment(error.position)
          }
        ),
        error
      );
    }

    return t('validation.feature_name', {
      name: error.feature
        ? t(`features.${error.feature}`)
        : t('validation.invalid_value')
    });
  };

  if (!errors.length) {
    return null;
  }

  return (
    <div className='relative flex flex-col ~gap-2/3'>
      <BaseButton
        variant='secondary'
        size='sm'
        onClick={() => setIsExpanded((prev) => !prev)}
        title={`${t('validation.view_errors')} (${errors.length})`}
        additionalStyles='capitalize ~px-3/6 !bg-red-200 !text-red-600 dark:!bg-red-900 dark:!text-red-300 !border-red-300 dark:!border-red-700'
        aria-expanded={isExpanded}
      />
      {isExpanded ? (
        <ul className='w-[50vw] md:w-[35vw] lg:w-[30vw] xl:w-[25vw] h-fit max-h-[50vh] ~p-2.5/4 flex flex-col ~gap-2/3 rounded-xl overflow-y-auto scrollbar-thin scrollbar-thumb-red-400 dark:scrollbar-thumb-secondary-dark-500 border border-red-300 dark:border-secondary-dark-500 bg-red-100 dark:bg-secondary-dark-200'>
          {errors.map((error, index) => {
            const positionLabel = getPositionLabel(error);
            const canNavigate = canNavigateToValidationError(error);

            return (
              <li
                key={`${error.tab.title}-${error.feature ?? 'unknown'}-${index}`}
                className='w-full flex flex-wrap ~gap-1/2 shadow-sm dark:bg-secondary-dark-300 dark:border dark:border-secondary-dark-500 bg-red-200/95 ~py-1.5/2 ~px-2/2.5 rounded-md'>
                <Chip
                  variant='info'
                  size='xs'
                  text={t(`validation.tab_routine`, {
                    routine: t(error.tab.title)
                  })}
                />
                {canNavigate ? (
                  <button
                    type='button'
                    className='inline-flex cursor-pointer hover:underline'
                    aria-label={t('validation.go_to_habit', {
                      name: error.habitName ?? positionLabel
                    })}
                    onClick={() => handleErrorClick(error)}>
                    <Chip variant='warning' size='xs' text={positionLabel} />
                  </button>
                ) : (
                  <Chip variant='warning' size='xs' text={positionLabel} />
                )}
                <Chip
                  variant='danger'
                  size='xs'
                  text={decodeHtmlEntities(
                    t('validation.error_message', {
                      message: error.message
                    })
                  )}
                />

                {error.invalid_value &&
                  error.invalid_value !== error.habitName && (
                    <Chip
                      variant='danger'
                      size='xs'
                      text={
                        createHtml(
                          t('validation.error_invalid_value', {
                            value: String(error.invalid_value)
                          })
                        ) as string
                      }
                    />
                  )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default ValidationErrors;
